// FILE: src/pages/StudentSupportPage.jsx
// The student's Support hub (plan WP-8): the three support channels with
// contact details and hours (MTO 10.1, 12.2), a way to send a new request
// (its own page, /student/support/new) and the student's requests with
// labelled filters (frontend-audit §4). Row links are MUI Links rendered by
// react-router (B-25) with a specific accessible name.
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Typography
} from "@mui/material";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import { fetchMyTickets } from "../features/supportSlice";
import { EmptyState, ErrorState, LoadingState, PageHeader, Section, visuallyHidden } from "../components/ui";
import { ChannelBlock, useAllSupportChannels } from "../components/student/SupportChannelsCard";
import {
  CategoryChip,
  TICKET_CATEGORIES,
  TICKET_STATUSES,
  TicketStatusChip,
  categoryIcon,
  formatDate,
  formatDateTime,
  ticketTitle
} from "../components/support/ticketDisplay";
import { ticketCategoryLabel, ticketStatusLabel } from "../i18n/labels";

function ChannelsSection() {
  const channels = useAllSupportChannels();
  return (
    <Section
      id="support-channels"
      title="Contact us"
      description="Three kinds of support, each with its own contact. Call or email the team that fits your question, during the hours shown."
    >
      {channels === null ? (
        <LoadingState variant="inline" label="Loading support contact details…" />
      ) : channels.length === 0 ? (
        <Typography>
          Support contact details are unavailable right now. Use “Ask for help” to send a request instead.
        </Typography>
      ) : (
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" } }}>
          {channels.map((c) => (
            <ChannelBlock key={c.type} channel={c} />
          ))}
        </Box>
      )}
    </Section>
  );
}

function TicketRow({ ticket }) {
  const Icon = categoryIcon(ticket.category);
  const title = ticketTitle(ticket);
  const updated = ticket.updatedAt && ticket.updatedAt !== ticket.createdAt ? formatDateTime(ticket.updatedAt) : null;
  return (
    <Box
      component="li"
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "auto minmax(0, 1fr)", md: "auto minmax(0, 1fr) auto" },
        gap: { xs: 1.5, md: 2.5 },
        alignItems: { xs: "start", md: "center" },
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper"
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2.5,
          display: "grid",
          placeItems: "center",
          bgcolor: "zun.primaryTint",
          color: "primary.main"
        }}
      >
        <Icon />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography component="h3" sx={{ fontWeight: 650, overflowWrap: "anywhere" }}>
          {title}
        </Typography>
        <Stack direction="row" useFlexGap spacing={1} sx={{ mt: 0.75, alignItems: "center", flexWrap: "wrap", rowGap: 0.75 }}>
          <TicketStatusChip status={ticket.status} />
          <CategoryChip category={ticket.category} />
        </Stack>
        <Typography sx={{ color: "text.secondary", mt: 0.75 }}>
          Sent {formatDate(ticket.createdAt)}
          {updated ? ` · Last update ${updated}` : ""}
        </Typography>
      </Box>

      <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" }, justifySelf: { xs: "start", md: "end" } }}>
        <Link component={RouterLink} to={`/student/support/tickets/${ticket._id}`} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
          View request
          <Box component="span" sx={visuallyHidden}>
            {` “${title}”, sent ${formatDate(ticket.createdAt)}`}
          </Box>
        </Link>
      </Box>
    </Box>
  );
}

function FilterSelect({ id, label, value, onChange, options }) {
  return (
    <FormControl sx={{ minWidth: { xs: "100%", sm: 220 } }}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select labelId={`${id}-label`} id={id} value={value} label={label} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const STATUS_OPTIONS = [{ value: "all", label: "All requests" }, ...TICKET_STATUSES.map((s) => ({ value: s, label: ticketStatusLabel(s) }))];
const CATEGORY_OPTIONS = [
  { value: "all", label: "All kinds of help" },
  ...TICKET_CATEGORIES.map((c) => ({ value: c, label: ticketCategoryLabel(c) }))
];

export default function StudentSupportPage() {
  const dispatch = useDispatch();
  const { tickets: rawTickets, loading, error } = useSelector((state) => state.support);
  const tickets = Array.isArray(rawTickets) ? rawTickets : [];
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const filtered = status !== "all" || category !== "all";

  const load = useCallback(() => {
    dispatch(fetchMyTickets({ status, category, sort: "createdAt", order: "desc" }));
  }, [dispatch, status, category]);

  useEffect(() => {
    load();
  }, [load]);

  const clearFilters = () => {
    setStatus("all");
    setCategory("all");
  };

  const countText =
    loading || error || tickets.length === 0
      ? ""
      : `${tickets.length === 1 ? "1 request" : `${tickets.length} requests`}${filtered ? " match your filters" : ""}`;

  let body;
  if (loading) {
    body = <LoadingState variant="table" rows={3} label="Loading your requests…" />;
  } else if (error) {
    body = <ErrorState error={error} title="We couldn't load your requests" onRetry={load} />;
  } else if (tickets.length === 0) {
    body = filtered ? (
      <EmptyState
        compact
        title="No requests match these filters"
        description="Try a different status or kind of help, or show all your requests."
        action={<Button onClick={clearFilters}>Show all requests</Button>}
      />
    ) : (
      <EmptyState
        title="You haven't asked for help yet"
        description="When you send a request, it appears here with our replies. We answer within 48 hours."
        action={
          <Button component={RouterLink} to="/student/support/new" variant="contained">
            Ask for help
          </Button>
        }
      />
    );
  } else {
    body = (
      <Stack component="ul" spacing={1.5} sx={{ listStyle: "none", m: 0, p: 0 }} aria-label="Your support requests">
        {tickets.map((t) => (
          <TicketRow key={t._id} ticket={t} />
        ))}
      </Stack>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Support"
        description="Get help with the website, your account or the course. We reply to every request within 48 hours."
        actions={
          <Button
            component={RouterLink}
            to="/student/support/new"
            variant="contained"
            size="large"
            startIcon={<AddCommentOutlinedIcon />}
          >
            Ask for help
          </Button>
        }
      />

      <Stack spacing={2}>
        <ChannelsSection />

        <Section id="support-requests" title="Your requests" description="Everything you've asked us, newest first.">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 2.5, alignItems: { xs: "stretch", sm: "center" } }}
            role="group"
            aria-label="Filter your requests"
          >
            <FilterSelect id="ticket-status-filter" label="Status" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
            <FilterSelect
              id="ticket-category-filter"
              label="Kind of help"
              value={category}
              onChange={setCategory}
              options={CATEGORY_OPTIONS}
            />
          </Stack>
          {/* Always mounted, so a filter change is announced once the list is back. */}
          <Typography role="status" sx={{ color: "text.secondary", mb: countText ? 1.5 : 0 }}>
            {countText}
          </Typography>
          {body}
        </Section>
      </Stack>
    </Box>
  );
}
